/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useContext, useEffect } from 'react';
import Media from 'react-media';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useForm, FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

// Helper
import { schemaForEditOccasion } from '../Helpers/Schema';

import { object, string, PropTypes, arrayOf, number, bool } from 'prop-types';
import { useMutation } from 'react-query';
import { useUIDSeed } from 'react-uid';

// MUI
import { Button, Grid } from '@material-ui/core';

// Icons
import leftarrowicon from '../Assets/leftarrowicon.svg';

// Helpers
import { RecipientBookContext } from '../Context/RecipientBookContext';
import { getAccessTokenSafely } from '../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getUserProfile } from '../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import CUSTOM_BREAKPOINTS from '../../../../../style/customBreakpoints';
import { isEmpty } from '../../../../../utils/object';
import { sortOccasionsByDate, maskPhoneNumber } from '../Helpers/Helpers';

// APIS
import { deleteRecipientProfile } from '../../../../../../../../apis/account-apis/RecipientBook-apis/deleteRecipientProfile';

// Components
import EditProfile from './EditProfile';
import EditOccasionsContainer from './EditOccasions/EditOccasionsContainer';
import AddressCard from './EditMailingAddress/AddressCard';
import AddNewMailingAddress from './EditMailingAddress/AddNewMailingAddress';
import DeleteDialog from './DeleteDialog';
import { relationshipMapping } from '../Helpers/Helpers';
import ErrorPage from '../../../../../common/ErrorPage';
import AdditionalOccasions from '../AddNewRecipient/AddOccasions/AdditionalOccasions';
import { commonUseStyles } from '../../../../../style/commonStyles';

// import StyledButton from '../../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

const DesktopEditRecipient = ({ classes, recipient, recipientBookListResError, selectedOccasionId = '' }) => {
    const { currentRecipient, setCurrentRecipient, isAddressEditing, setIsOccasionsEditing,
        setIsAddressEditing, setIsProfileEditing, setSelectedOccasionToEdit, setSelectedAddressToEdit, addNewAddressClicked,
        setAddNewAddressClicked, setShowMoreOccasions, setCloseBtnClicked, setIsEditingRecipient,
    } = useContext(RecipientBookContext);
    const [deleteProfileClicked, setDeleteProfileClicked] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const userProfile = useSelector(getUserProfile) || {};
    const { ContactId } = userProfile || '';
    const seed = useUIDSeed();
    const dispatch = useDispatch();
    const history = useHistory();
    const breakpointExpr = `(max-width: ${(CUSTOM_BREAKPOINTS.tabLandscape)}px)`;
    const customClasses = commonUseStyles();

    useEffect(() => {
        setCurrentRecipient(recipient?.addressBookResponse?.addressList?.[0]);
    }, [recipient]);

    useEffect(() => {
        if (recipientBookListResError === 'NO_RECIPIENT_FOUND') {
            setCurrentRecipient({});
        }
    }, [recipientBookListResError]);

    // TODO: Review if filtering addresses here for only ones with addressLineOne, city, state, zip and country is necessary,
    // because in an edit address flow there is no reason to edit a blank address. That should be add address flow
    const addresses = currentRecipient?.addresses?.length ? currentRecipient?.addresses?.filter((address) => address.addressLineOne && address.city && address.state && address.zip && address.country) : [];
    const isBirthdayOccasionExisting = currentRecipient?.occasions?.find((recipOccasion) => recipOccasion?.occasion === '2');
    const recipientRelationship = relationshipMapping[currentRecipient?.relationship];
    const reminderPhone = maskPhoneNumber(currentRecipient?.occasions?.[0]?.reminderPhone || recipient?.addressBookResponse?.reminderPhone || '');
    const [switchStatus, setSwitchStatus] = useState(); // Note: Do not remove this state, this is required to update occasions on switch status on switch change
    // TECH DEBT **
    // since there are some issues with getting rcpId consistently for old and new recipients at the top level,
    // we should grab rcpId from either the top level or the addresses[0] object
    const rcpId = currentRecipient?.rcpId || currentRecipient?.addresses?.[0]?.rcpId;

    let occasions = currentRecipient?.occasions;
    if (occasions === undefined || !occasions?.length) {
        occasions = [{
            occasion: '2', desc: 'Birthday', dateMM: '', dateDD: '', dateYYYY: '', occasionId: '', subscribedEmail: true, subscribedPhone: false, reminderPhone, reminderEmail: userProfile?.email || '', subscribed: true,
        }]
    } else if (!isBirthdayOccasionExisting) {
        occasions = [...currentRecipient?.occasions, {
            occasion: '2', desc: 'Birthday', dateMM: '', dateDD: '', dateYYYY: '', occasionId: '', subscribedEmail: true, subscribedPhone: false, reminderPhone, reminderEmail: userProfile?.email || '', subscribed: true,
        }]
    }

    occasions = occasions.map((occ) => {
        if (occ.reminderPhone) {
            occ.reminderPhone = reminderPhone;
        } else {
            occ['reminderPhone'] = reminderPhone;
        }
        return occ;
    });

    const methods = useForm({ mode: 'all', defaultValues: { occasions }, resolver: joiResolver(schemaForEditOccasion) });
    const watchOccasions = methods.watch('occasions');

    // Sorting moved here from EditOccasionsContainer just to avoid jumping occasions sorting while occasions changing
    const sortedOccasions = sortOccasionsByDate(occasions);

    useEffect(() => {
        if (currentRecipient?.occasions?.length) {
            methods.setValue('occasions', [...sortedOccasions]);
        }
    }, [currentRecipient?.occasions]);

    // Commenting out below code untill we 100% sure not requrired this code anymore
    // const findAndDeleteRecipient = (deletedRecipientId) => {
    //     const stateStateCopy = { ...recipient };
    //     const filteredData = stateStateCopy?.addressBookResponse?.addressList?.filter((recip) => recip.rcpId !== deletedRecipientId)
    //     setRecipientState({ ...recipientState, addressBookResponse: { addressList: filteredData } })
    // }

    const getStatusOfSwitch = (switchStatus, index) => {
        methods.setValue(`occasions[${index}].subscribedEmail`, switchStatus);
        setSwitchStatus(switchStatus);
        if (switchStatus === false) {
            methods.setValue(`occasions[${index}].subscribedPhone`, switchStatus);
        };
    };

    const getUpdatedOccasions = (updatedOccasions) => { // Note: please do not remove this logic any way, this is really required for additional occasions work correctly.
        occasions = updatedOccasions;
    };

    const deleteRecipientProfileMutation = useMutation({
        mutationFn: deleteRecipientProfile,
        onSuccess: (data) => {
            if (data && data.status === 200 && data.data !== undefined) {
                setIsEditingRecipient(false);
                history.push('/account/address-book');
                // Commenting out below code untill we 100% sure not requrired this code anymore
                // const url = data?.config?.url || ''
                // const splitUrl = url.split('/')
                // const deletedRecipientId = splitUrl[splitUrl.length - 1];
                // findAndDeleteRecipient(deletedRecipientId)
            }
            setLoading(false);
            setDeleteProfileClicked(false);
        },
        onError: () => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            // mbpLogger.logError({
            //     function: 'deleteRecipientProfileMutation',
            //     appName: process.env.npm_package_name,
            //     module: 'EditRecipient.js',
            //     message: `Error While Deleting Recipient with Recipient Id: ${currentRecipient?.rcpId}`,
            //     err,
            // });
        },
    });

    const deleteRecipient = async () => {
        setLoading(true);
        dispatch(trackEvent({
            eventName: "contact_edit_interaction",
            edit_action_type: "Delete Contact",
            eventCategory: "Address Book",
            eventAction: "Contact Edit",
            eventLabel: "Contact Delete"
        }))
        const jwtToken = await getAccessTokenSafely();
        deleteRecipientProfileMutation.mutate({
            rcpId: currentRecipient.rcpId,
            ContactId,
            jwtToken,
        });
    };

    if (recipientBookListResError === 'GENERIC_API_ERROR') {
        return (
            <Media query={breakpointExpr}>
                {(matches) => (
                    matches ? (
                        <div>
                            <div className={customClasses.orderContainer}>
                                <h1>
                                    Contacts
                                </h1>
                            </div>
                            <ErrorPage errors={error} />
                        </div>
                    ) : (
                        <>
                            <Grid item xs={10} className={customClasses.gridItem}>
                                <div className={customClasses.orderContainer}>
                                    <h1>
                                        Contacts
                                    </h1>
                                </div>
                                <ErrorPage errors={error} />
                            </Grid>
                        </>
                    )
                )}
            </Media>
        );
    }

    useEffect(() => {
        // Scroll to top when loading Edit Recipient
        window.scrollTo({
            top: 0,
            left: 0,
        })
    }, [])

    return (
        <Grid className={classes.containerRecipient}>
            <Grid item sx={{ padding: '20px 24px 0px 20px' }}>
                <div className={classes.backButtonContainer}
                    onClick={() => {
                        setIsEditingRecipient(false);
                        setCurrentRecipient({});
                        setIsProfileEditing(false);
                        setIsOccasionsEditing(false);
                        setSelectedOccasionToEdit('');
                        setIsAddressEditing(false);
                        setSelectedAddressToEdit('');
                        setAddNewAddressClicked(false);
                        setCloseBtnClicked(false);
                        setShowMoreOccasions(false);
                        history.push('/account/address-book');
                    }} role="button" tabIndex={0} onKeyDown={() => { }}>
                    <span><img src={leftarrowicon} alt="left arrow icon" /></span>
                    <span className={classes.backButtonIcon}>Contacts</span>
                </div>
                {!isEmpty(currentRecipient) ?
                    (<>
                        <Grid container className={classes.addNewRecipientWrapper}>
                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <Grid container>
                                <div className="formStyle">
                                    <EditProfile
                                        key={`${currentRecipient?.rcpFirstName}-${currentRecipient?.rcpLastName}-${currentRecipient?.rcpId}`}
                                        recipient={currentRecipient}
                                        recipientRelationship={recipientRelationship}
                                        classes={classes}
                                        parentComponent="EditRecipient"
                                    />
                                    <EditOccasionsContainer
                                        key={`${seed(occasions)}`}
                                        occasions={occasions}
                                        classes={classes}
                                        rcpId={rcpId}
                                        rcpFirstName={currentRecipient?.rcpFirstName}
                                        rcpLastName={currentRecipient?.rcpLastName}
                                        isParentDesktopComponent={true}
                                        selectedOccasionId={selectedOccasionId}
                                        getUpdatedOccasions={getUpdatedOccasions}
                                        sortedOccasions={sortedOccasions}
                                    />
                                    <FormProvider {...methods}>
                                        <AdditionalOccasions
                                            classes={classes}
                                            rcpId={rcpId}
                                            occasions={watchOccasions}
                                            occasionsList={occasions}
                                            rcpFirstName={currentRecipient?.rcpFirstName}
                                            rcpLastName={currentRecipient?.rcpLastName}
                                            switchStatus={switchStatus}
                                            getStatusOfSwitch={getStatusOfSwitch}
                                            reminderPhone={reminderPhone}
                                        />
                                    </FormProvider>
                                    {addresses?.map((address, index) => !address?.isBillingAddress && <AddressCard key={`${index}-${address.addressBookEntryId}-${address.rcpId}`} address={address} index={index} classes={classes} isParentDesktopComponent={true} />)}
                                </div>
                            </Grid>
                        </Grid>
                        <Grid container direction="column" alignContent='center'>
                            {!addNewAddressClicked || isAddressEditing
                                ? (
                                    <Grid item>
                                        <Button
                                            // variant="outlined"
                                            // color="primary"
                                            fullWidth
                                            size='medium'
                                            className={`${classes.outlinedPrimaryBtn} ${classes.gridItem} text-transform-none`}
                                            onClick={() => {
                                                dispatch(trackEvent({
                                                    eventName: "contact_edit_interaction",
                                                    edit_action_type: "Add Address",
                                                    eventCategory: "Address Book",
                                                    eventAction: "Contact Edit",
                                                    eventLabel: "Address Add"
                                                }))
                                                setIsProfileEditing(false); // setting global occasion state false to keep close all other edit screens
                                                setIsOccasionsEditing(false); // setting global occasion state false to keep close all other edit screens
                                                setSelectedAddressToEdit('');
                                                setSelectedOccasionToEdit('');
                                                setShowMoreOccasions(false);
                                                setAddNewAddressClicked(true); // this is to handle local state to open and close, todo: will remove local state later after proper testing.
                                                setIsAddressEditing(false); // setting global address state true to keep add address form open
                                            }}
                                        >
                                            Add Address
                                        </Button>
                                        {
                                            /*
                                                This EDS button styles are being overriden by the button styles inside main.js
        
                                                We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                                is not protected. Disabling this button for now.
                                            */
                                        }
                                        {/* <StyledButton
                                            variant="secondary"
                                            fullWidth
                                            label="Add Address"
                                            handleOnClick={() => {
                                                dispatch(trackEvent({
                                                    eventName: "contact_edit_interaction",
                                                    edit_action_type: "Add Address",
                                                    eventCategory: "Address Book",
                                                    eventAction: "Contact Edit",
                                                    eventLabel: "Address Add"
                                                }))
                                                setIsProfileEditing(false); // setting global occasion state false to keep close all other edit screens
                                                setIsOccasionsEditing(false); // setting global occasion state false to keep close all other edit screens
                                                setSelectedAddressToEdit('');
                                                setSelectedOccasionToEdit('');
                                                setAddNewAddressClicked(true); // this is to handle local state to open and close, todo: will remove local state later after proper testing.
                                                setIsAddressEditing(false); // setting global address state true to keep add address form open
                                            }}
                                        /> */}
                                    </Grid>
                                )
                                : (
                                    <>
                                        {!isAddressEditing && addNewAddressClicked && (
                                            <AddNewMailingAddress
                                                key={`${currentRecipient.rcpFirstName}-${currentRecipient.rcpLastName}-${currentRecipient?.addresses?.[0]?.addressBookEntryId}`}
                                                classes={classes}
                                                setAddNewAddressClicked={setAddNewAddressClicked}
                                                recipient={currentRecipient}
                                            />)}
                                    </>
                                )}
                            {deleteProfileClicked && (
                                <DeleteDialog
                                    classes={classes}
                                    loading={loading}
                                    error={error}
                                    setError={setError}
                                    isShowingDeleteDialog={deleteProfileClicked}
                                    setIsShowingDeleteDialog={setDeleteProfileClicked}
                                    handleClick={deleteRecipient}
                                    description="Contact"
                                />
                            )}
                            <Grid item className={classes.buttonStyle}>
                                <Button
                                    fullWidth
                                    className={classes.deleteBtn}
                                    onClick={() => setDeleteProfileClicked(true)}
                                >
                                    Delete This Contact
                                </Button>
                            </Grid>
                        </Grid>
                    </>)
                    : (<p className={classes.noRecipient}>No Recipeint Found</p>)
                }
            </Grid>
        </Grid>
    );
};

DesktopEditRecipient.propTypes = {
    recipient: PropTypes.shape({
        addressBookResponse: PropTypes.shape({
            addressList: arrayOf(PropTypes.shape({
                addresses: arrayOf(PropTypes.shape({
                    addressBookEntryId: string,
                    addressLineOne: string,
                    addressType: string,
                    city: string,
                    country: string,
                    firstName: string,
                    isBillingAddress: bool,
                    lastName: string,
                    lastUpdateDate: string,
                    phoneNumber: string,
                    rcpId: string,
                    state: string,
                    zip: string,
                })),
                occasions: arrayOf(PropTypes.shape({
                    desc: string,
                    rcpId: string,
                    dateDD: string,
                    dateMM: string,
                    dateYYYY: string,
                    subscribed: bool,
                    occasion: string,
                    occasionId: string,
                    createdDate: string,
                    createdUser: string,
                    reminderEmail: string,
                    reminderPhone: string,
                    subscribedEmail: bool,
                    subscribedPhone: bool,
                })),
                rcpAddressBookEntryId: string,
                rcpFirstName: string,
                rcpId: string,
                rcpLastName: string,
            })),
            contactId: string,
            reminderEmail: string,
            totalEntries: number,
        }),
    }),
    selectedOccasionId: string,
    recipientBookListResError: string,
    // This declaration is useless if we don't specify the shape of the object. `currentRecipient` from the `useContext` hook is {} by default.
    classes: object.isRequired,
};

DesktopEditRecipient.defaultProps = {
    selectedOccasionId: '',
};


export default DesktopEditRecipient;